import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import type { User, Tenant } from '../api/auth';
import { logout as logoutAPI } from '../api/auth';
import { getLastRefreshFailure, refreshOnce } from '../api/client';
import {
  clearAuthState,
  hasSessionHint,
  isTokenExpired,
  removeLegacyTokenKeys,
} from '../utils/authHelpers';
import { getAccessToken, setAccessToken } from '../auth/accessToken';
import { onAuthMessage } from '../auth/authChannel';

// Story 22.8 (AMA-380) — first-week-user detection capture (per-device).
// Stamps `ama-first-seen-at-${userId}` on first authenticated mount so
// `useFirstWeekUser` can compute (now - firstSeenAt) < 7d. Idempotent.
// eslint-disable-next-line react-refresh/only-export-components -- first-seen helper owned by the auth context; 4 other importers
export function captureFirstSeen(userId: string | undefined | null): void {
  if (!userId) return;
  try {
    const key = `ama-first-seen-at-${userId}`;
    if (!localStorage.getItem(key)) {
      localStorage.setItem(key, new Date().toISOString());
    }
  } catch {
    // localStorage may be unavailable (SSR, quota-exceeded, private browsing).
    // First-week-user detection degrades gracefully — non-first-week treatment.
  }
}

/** Back-off before each retry of a transiently failed mount refresh. */
const MOUNT_REFRESH_RETRY_DELAYS_MS = [1_000, 3_000] as const;

/**
 * The mount refresh, retried while it fails transiently. Stops at the first
 * success or rejection. Never throws.
 */
async function refreshOnMount(): Promise<string | null> {
  let token = await refreshOnce().catch(() => null);
  for (const delay of MOUNT_REFRESH_RETRY_DELAYS_MS) {
    if (token || getLastRefreshFailure() !== 'transient') break;
    await new Promise((resolve) => setTimeout(resolve, delay));
    token = await refreshOnce().catch(() => null);
  }
  return token;
}

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  tenant: Tenant | null;
  accessToken: string | null;
  /**
   * Story 17.2 — the refresh token is an HttpOnly cookie the SPA never sees, so
   * `login` takes only the access token (kept in memory) plus identity.
   */
  login: (accessToken: string, user: User, tenant: Tenant) => void;
  logout: () => void;
  refreshAccessToken: () => Promise<string | null>;
}

/* eslint-disable react-refresh/only-export-components */

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/** Parse the `user` / `tenant` identity JSON, or `null` when absent / corrupt. */
function readStoredIdentity(): { user: User; tenant: Tenant } | null {
  try {
    const storedUser = localStorage.getItem('user');
    const storedTenant = localStorage.getItem('tenant');
    if (!storedUser || !storedTenant) return null;
    const user = JSON.parse(storedUser) as User | null;
    const tenant = JSON.parse(storedTenant) as Tenant | null;
    if (!user || typeof user !== 'object' || !tenant || typeof tenant !== 'object') return null;
    return { user, tenant };
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [accessToken, setAccessTokenState] = useState<string | null>(null);

  // Rehydrate on mount (Story 17.2). The access token lives in memory only, so
  // a reload or a new tab starts without one: obtain it through `refreshOnce`,
  // which the HttpOnly refresh cookie authenticates and which writes the
  // response's user/tenant to localStorage. `refreshOnce` single-flights with
  // the interceptor and chat transport, defers to / adopts a peer tab's refresh
  // over the `amatis-auth` channel, and never throws: only a `null` is
  // terminal. The `.catch` mirrors chatFetch.ts. A failure here means
  // "not signed in" — no redirect (ProtectedRoute does that), so /login can
  // never loop. Only a REJECTED refresh (401, peer logout) clears the session.
  // A transient one (harness restart 502, 429, network drop) is retried, and
  // if it still fails the tab shows signed out but keeps the session hint, so
  // the next reload tries the still-valid cookie again.
  useEffect(() => {
    const loadAuthState = async () => {
      try {
        let token = getAccessToken();
        if (!token || isTokenExpired(token)) {
          token = hasSessionHint() ? await refreshOnMount() : null;
        }
        const identity = token ? readStoredIdentity() : null;
        if (token && identity) {
          setAccessTokenState(token);
          setUser(identity.user);
          setTenant(identity.tenant);
          captureFirstSeen(identity.user?.id);
          setIsAuthenticated(true);
        } else if (!token && getLastRefreshFailure() === 'transient') {
          setIsAuthenticated(false);
        } else {
          logout();
        }
      } catch (error) {
        console.error('Failed to load auth state:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadAuthState();
  }, []);

  // Multi-tab sync (Story 2.6; Story 17.2 moved it onto the BroadcastChannel).
  // A peer's `refreshed` carries its new token + identity — adopt them (the api
  // client has already put the token in memory). A peer's `logout` ends this
  // tab's session too.
  useEffect(() => {
    return onAuthMessage((message) => {
      if (message.type === 'refreshed') {
        setAccessTokenState(message.accessToken);
        if (message.user) setUser(message.user);
        if (message.tenant) setTenant(message.tenant);
        if (message.user && message.tenant) setIsAuthenticated(true);
      } else if (message.type === 'logout') {
        logout();
        window.location.href = '/login';
      }
    });
  }, []);

  // Automatic token expiration check and logout (Story 2.6, AC-2.6.4)
  useEffect(() => {
    const checkTokenExpiration = async () => {
      const currentAccessToken = getAccessToken();

      if (!currentAccessToken || !isAuthenticated) return;

      try {
        // Check if access token is expired
        if (isTokenExpired(currentAccessToken)) {
          console.log('Access token expired, attempting refresh...');
          // Access token expired - attempt refresh
          const newToken = await refreshAccessToken();

          if (!newToken) {
            // Refresh failed - force logout
            console.log('Token refresh failed, forcing logout');
            // Call the logout API function to invalidate tokens server-side
            await logoutAPI();
          }
        }
      } catch (error) {
        console.error('Token expiration check error:', error);
        // On error, force logout for security
        await logoutAPI();
      }
    };

    // Check on mount and every 5 minutes
    checkTokenExpiration();
    const interval = setInterval(checkTokenExpiration, 5 * 60 * 1000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const login = (newAccessToken: string, newUser: User, newTenant: Tenant) => {
    // The token goes to memory only; the refresh token is already in the
    // HttpOnly cookie the login response set. Any pre-17.2 keys are dropped.
    setAccessToken(newAccessToken);
    removeLegacyTokenKeys();
    localStorage.setItem('user', JSON.stringify(newUser));
    localStorage.setItem('tenant', JSON.stringify(newTenant));

    setAccessTokenState(newAccessToken);
    setUser(newUser);
    setTenant(newTenant);
    captureFirstSeen(newUser?.id);
    setIsAuthenticated(true);
  };

  const logout = () => {
    // Clear the memory token + identity using the shared helper
    clearAuthState();

    // Clear React state
    setAccessTokenState(null);
    setUser(null);
    setTenant(null);
    setIsAuthenticated(false);
  };

  // Routed through the api client's single-flight `refreshOnce` (T-20260723-17):
  // with 30-minute access tokens this sweep fires often, and a second raw
  // refresh racing the axios interceptor or chat transport would lose the
  // single-use rotation and log the user out. `refreshOnce` puts the new token
  // in memory itself and never throws.
  const refreshAccessToken = async (): Promise<string | null> => {
    const newToken = await refreshOnce();
    if (!newToken) {
      logout();
      return null;
    }
    setAccessTokenState(newToken);
    return newToken;
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        user,
        tenant,
        accessToken,
        login,
        logout,
        refreshAccessToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
